import * as React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import { Button, Container, Dialog, DialogContent, IconButton, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Database, LocationLanguageContextType, NovadeLiteUrlContextType } from 'typings';
import ReactMarkdown from 'react-markdown';
import { Languages } from '../../utils/getAllSupportedLanguages';
import { LocationLanguageContext } from '../../contexts/LocationLanguageContext';
import useDialogStyles from '../../styles/Dialog';
import { NovadeLiteUrlContext } from '../../contexts/NovadeLiteUrlContext';
import { matchesMobile } from '../../utils/mediaQuery';
import { getLoginRedirectUrl, getRegion } from '../../utils/common';

interface TemplatePaywallDialogProps {
  open: boolean;
  workspace: Database;
  onDialogClose: () => void;
}

const strapiTemplatePaywallDialogQuery = graphql`
  query TemplatePaywallDialogQuery {
    allStrapiTemplatePaywallDialog {
      nodes {
        TitleOne
        TitleTwo
        PlanUpgradeLabel
        LearnMoreLabel
        LearnMoreUrl
        TitleOneMobile
        TitleTwoMobile
        Description
        locale
      }
    }
  }
`;

const separateSentences = (text: string) =>
  text
    .split('.')
    .filter(Boolean)
    .map((sentence: string, idx: number, arr: string[]) => {
      const key = sentence.trim().slice(0, 20) + idx;
      return idx < arr.length - 1 ? (
        <React.Fragment key={key}>
          {sentence.trim()}.
          <br />
        </React.Fragment>
      ) : (
        `${sentence.trim()}.`
      );
    });

const getTitle = (isMobile: boolean, dialogContent: any, manageSubscription: boolean) => {
  if (isMobile) {
    return manageSubscription ? dialogContent?.TitleOneMobile : dialogContent?.TitleTwoMobile;
  }
  const titleString = manageSubscription ? dialogContent.TitleOne : separateSentences(dialogContent.TitleTwo);

  return titleString;
};

const TemplatePaywallDialog: React.FC<TemplatePaywallDialogProps> = ({ open, workspace, onDialogClose }) => {
  const { manageSubscription } = workspace;
  const data = useStaticQuery(strapiTemplatePaywallDialogQuery);
  const dialogContents = data.allStrapiTemplatePaywallDialog.nodes;

  const locationLanguage = React.useContext<LocationLanguageContextType | null>(LocationLanguageContext);
  const language: string = locationLanguage?.language ?? Languages.English;
  const dialogContent = dialogContents.find((content: any) => content.locale === language) || dialogContents[0];
  if (!dialogContent) return null;

  const contextUrl = React.useContext<NovadeLiteUrlContextType>(NovadeLiteUrlContext);
  const { novadeLiteUrl } = contextUrl;
  const isMobile = matchesMobile();
  const title = getTitle(isMobile, dialogContent, manageSubscription ?? false);

  const region = getRegion(novadeLiteUrl);
  const loginRedirectUrl = getLoginRedirectUrl(region);

  const handleClose = () => {
    onDialogClose();
  };

  const classes = useDialogStyles();

  return (
    <Dialog
      maxWidth='md'
      onClose={handleClose}
      aria-label='Paywall Dialog'
      aria-labelledby='paywall-dialog-title'
      aria-describedby='paywall-dialog-description'
      open={open}
      PaperProps={{ classes: { root: classes.dialogPaper } }}
    >
      <div className={classes.dialogClose}>
        <IconButton aria-label='close' onClick={handleClose} className='close-icon-button'>
          <CloseIcon />
        </IconButton>
      </div>
      <DialogContent className={classes.dialogContent}>
        <Container>
          <Container className={classes.imgDiv}>
            {isMobile ? (
              <StaticImage layout='fixed' src='../../assets/forms_Illustration.png' alt='Novade' width={200} />
            ) : (
              <StaticImage layout='fixed' src='../../assets/forms_Illustration.png' alt='Novade' width={300} />
            )}
          </Container>
          <Container>
            <Typography id='paywall-dialog-title' variant='h6'>
              {title}
            </Typography>
          </Container>
          <Container maxWidth='md'>
            <Typography component='div' id='paywall-dialog-description' className={classes.description}>
              <ReactMarkdown className='markdown-text'>{dialogContent.Description}</ReactMarkdown>
            </Typography>
          </Container>
          {manageSubscription && !isMobile && (
            <Container>
              <Button
                variant='contained'
                href={loginRedirectUrl}
                className={classes.nlBtn}
                target='_blank'
                rel='noreferrer'
                disabled={!dialogContent?.PlanUpgradeLabel}
              >
                {dialogContent?.PlanUpgradeLabel}
              </Button>
            </Container>
          )}
          <Typography variant='body2'>
            <a href={dialogContent?.LearnMoreUrl || '#'} className={classes.learnBtn} target='_blank' rel='noreferrer'>
              {dialogContent.LearnMoreLabel}
            </a>
          </Typography>
        </Container>
      </DialogContent>
    </Dialog>
  );
};

export default TemplatePaywallDialog;
